 

// On réassigne state mais c'est prévu par redux-toolkit
/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["state"] }] */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EmployeSignatureGetData } from '@services/Schemas/EmployeSignatureGet';
import signatureApi from '@services/signatureApi';
import { FormulairePourSignatureGetData, SubmitFormulaireResult, SubmitDemandeAdhesionResult } from '@services/Schemas/Formulaires';
import formulaireApi from '@services/formulaireApi';
import { sanitizeDateString } from '@utils/dateUtils';
import { RootState } from './store';

const sliceName = 'signature';

export interface DemissionInfo { image: string, date: string, syndicat: string }

export interface SignatureSlice {
  currentEtapeIndex: number;
  employeInfo?: EmployeSignatureGetData;
  formulaire?: FormulairePourSignatureGetData;
  infoMAJ: boolean;
  adhesion?: string;
  idUnique?: string;
  signatureCarte?: string;
  signatureDemission: Record<string, DemissionInfo>;
  valeursChampUtilisateur: Record<string, string>;
  verificationToken?: string;
  emailVerified: boolean;
  isSubmitted: boolean;
  submissionResult?: SubmitFormulaireResult;
  demandeAdhesionResult?: SubmitDemandeAdhesionResult;
  paymentToken?: string;
  isPaid: boolean;
}

const initialState: SignatureSlice = {
  currentEtapeIndex: 0,
  infoMAJ: false,
  signatureDemission: {},
  valeursChampUtilisateur: {},
  emailVerified: false,
  isSubmitted: false,
  isPaid: false,
};

export const signatureSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    goToNextEtape: (state) => {
      state.currentEtapeIndex += 1;
    },
    goToPreviousEtape: (state) => {
      state.currentEtapeIndex -= 1;
      if (state.currentEtapeIndex < 0) {
        state.currentEtapeIndex = 0;
      }

    },


    updateInfosPerso: (state, action: PayloadAction<EmployeSignatureGetData>) => {
      // Sanitize dateNaissance: convert empty string to undefined
      const sanitizedPayload = {
        ...action.payload,
        dateNaissance: sanitizeDateString(action.payload.dateNaissance),
      };
      
      state.employeInfo = {
        ...sanitizedPayload,
        // config: state.employeInfo?.config,
        consentements: state.employeInfo?.consentements ?? [],
        employeurNom: state.employeInfo?.employeurNom ?? '',
        idUnique: state.employeInfo?.idUnique ?? '',
        syndicatsMaraudeur: state.employeInfo?.syndicatsMaraudeur ?? [],
      };
      state.infoMAJ = true;
    },
    setValeursChampUtilisateur: (state, action: PayloadAction<Record<string, string>>) => {
      state.valeursChampUtilisateur = action.payload;
    },
    setSignatureCarte: (state, action: PayloadAction<string | undefined>) => {
      state.signatureCarte = action.payload;
    },
    setSignatureDemission: (state, action: PayloadAction<DemissionInfo>) => {
      state.signatureDemission[action.payload.syndicat] = action.payload;
    },
    removeSignatureDemission: (state, action: PayloadAction<string>) => {
      delete state.signatureDemission[action.payload];
    },

    setAdhesion: (state, action: PayloadAction<string>) => {
      state.adhesion = action.payload;
    },
    setConsentement: (state, action: PayloadAction<{ id: number, value: boolean }>) => {
      if (!state.employeInfo) return;
      const { id, value } = action.payload;
      const existing = state.employeInfo.consentements.find(x => x.objetDeConsentementId === id);
      if (existing) {
          existing.consent = value;
      } else {
          state.employeInfo.consentements.push({objetDeConsentementId: id, consent: value});
      }


    },
    setVerificationToken: (state, action: PayloadAction<string>) => {
      state.verificationToken = action.payload;
      state.emailVerified = true;
    },
    setSyndicatsMaraudeur: (state, action: PayloadAction<string[]>) => {
      if (state.employeInfo) {
        state.employeInfo.syndicatsMaraudeur = action.payload;
      }
    },
    setSubmissionResult: (state, action: PayloadAction<SubmitFormulaireResult>) => {
      state.isSubmitted = true;
      state.submissionResult = action.payload;
      if (action.payload.paymentToken) {
        state.paymentToken = action.payload.paymentToken;
      }
    },
    setDemandeAdhesionResult: (state, action: PayloadAction<SubmitDemandeAdhesionResult>) => {
      state.isSubmitted = true;
      state.demandeAdhesionResult = action.payload;
      if (action.payload.paymentToken) {
        state.paymentToken = action.payload.paymentToken;
      }
    },
    setPaymentCompleted: (state) => {
      state.isPaid = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(formulaireApi.endpoints.getFormulaireByRoute.matchFulfilled, (state, action) => {
      state.formulaire = action.payload;
    });

    builder.addMatcher(
      signatureApi.endpoints.getEmployeSignature.matchFulfilled,
      (state, action) => {
        // Sanitize dateNaissance: convert empty string to undefined
        state.employeInfo = {
          ...action.payload,
          dateNaissance: sanitizeDateString(action.payload.dateNaissance),
        };
        state.idUnique = action.meta.arg.originalArgs.idUnique;

      },
    );
  },
});

export const {
  goToPreviousEtape,
  goToNextEtape,
  updateInfosPerso,
  setSignatureCarte,
  setSignatureDemission,
  removeSignatureDemission,
  setValeursChampUtilisateur,
  setAdhesion,
  setConsentement,
  setVerificationToken,
  setSyndicatsMaraudeur,
  setSubmissionResult,
  setDemandeAdhesionResult,
  setPaymentCompleted,
} = signatureSlice.actions;
export const accountSliceName = signatureSlice.name;

export const selectCurrentEtapeIndex = (state: RootState) => state.signature.currentEtapeIndex;
export const selectEmployeInfo = (state: RootState) => state.signature.employeInfo;
export const selectSignatureCarte = (state: RootState) => state.signature.signatureCarte;
export const selectSignatureDemission = (state: RootState) => state.signature.signatureDemission;
export const selectAdhesion = (state: RootState) => state.signature.adhesion;
export const selectValeursChampUtilisateur = (state: RootState) => state.signature.valeursChampUtilisateur;
export const selectFormulaire = (state: RootState) => state.signature.formulaire;
export const selectVerificationToken = (state: RootState) => state.signature.verificationToken;
export const selectEmailVerified = (state: RootState) => state.signature.emailVerified;
export const selectIsSubmitted = (state: RootState) => state.signature.isSubmitted;
export const selectSubmissionResult = (state: RootState) => state.signature.submissionResult;
export const selectDemandeAdhesionResult = (state: RootState) => state.signature.demandeAdhesionResult;
export const selectPaymentToken = (state: RootState) => state.signature.paymentToken;
export const selectIsPaid = (state: RootState) => state.signature.isPaid;

export default signatureSlice.reducer;

